import React from "react";
import SkeletonLoader from "components/ui/SkeletonLoader";
import './UserConnectionSkeleton.css';

/**
 * Skeleton for the candidate list inside UserConnections.
 * Only renders the candidate cards — the header bar (title/search/filter)
 * and tab bar (Matches/Sent/Received) are already rendered by the parent.
 */

interface UserConnectionSkeletonProps {
  cards?: number;
}

const CandidateCardSkeleton: React.FC = () => (
  <div
    style={{
      background: "#ffffff",
      border: "1px solid #e5e7eb",
      borderRadius: 16,
      padding: "16px 20px",
      marginBottom: 12,
      display: "flex",
      alignItems: "center",
      gap: 16,
      boxShadow: "0 2px 8px rgba(41,41,41,0.05)",
    }}
  >
    {/* Avatar + match ring */}
    <div style={{ position: "relative", flexShrink: 0, width: 64, height: 64 }}>
      <SkeletonLoader circle width={64} height={64} />
      <div style={{ position: "absolute", bottom: -4, left: "50%", transform: "translateX(-50%)" }}>
        <SkeletonLoader width={34} height={18} style={{ borderRadius: 999 }} />
      </div>
    </div>

    {/* Main content */}
    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 7 }}>
      {/* Name + premium badge */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <SkeletonLoader width={140} height={16} />
        <SkeletonLoader width={64} height={18} style={{ borderRadius: 999 }} />
      </div>
      {/* Title + experience */}
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <SkeletonLoader width={160} height={13} />
        <SkeletonLoader width={60} height={13} />
      </div>
      {/* Meta: location · distance · connections */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        <SkeletonLoader width={180} height={12} />
        <SkeletonLoader width={80} height={12} />
        <SkeletonLoader width={90} height={12} />
      </div>
      {/* Chips: status · Resume · Pitch */}
      <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
        <SkeletonLoader width={110} height={26} style={{ borderRadius: 999 }} />
        <SkeletonLoader width={80} height={26} style={{ borderRadius: 999 }} />
        <SkeletonLoader width={60} height={26} style={{ borderRadius: 999 }} />
      </div>
    </div>

    {/* Right: action button + links */}
    <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
      <SkeletonLoader width={130} height={36} style={{ borderRadius: 999 }} />
      <div style={{ display: "flex", gap: 10 }}>
        <SkeletonLoader width={72} height={14} />
        <SkeletonLoader width={80} height={14} />
      </div>
    </div>
  </div>
);

const UserConnectionSkeleton: React.FC<UserConnectionSkeletonProps> = ({ cards = 4 }) => (
  <div style={{ padding: "0 12px" }}>
    {Array.from({ length: cards }).map((_, i) => (
      <CandidateCardSkeleton key={i} />
    ))}
  </div>
);

export default UserConnectionSkeleton;
